import {
  AvailableFeatures,
  ManagerAccountMe,
} from '@/domain/models/AuthEnterprise';
import { Enterprise } from '@/domain/models/Enterprise';
import {
  canUseMosPosMenuMasterPage,
  canUseMosPosResendPage,
} from '@/lib/AccessControl';
import { routes } from '@/router';

export type ChildList = {
  title: string;
  path: string;
  isDisabled: boolean;
};

type SidebarItem = {
  childPageList: ChildList[];
};

export type Sidebar = Record<
  | 'ordersData'
  | 'camelOrder'
  | 'camelKiosk'
  | 'menuManagement'
  | 'store'
  | 'external'
  | 'setting'
  | 'delivery'
  | 'salesReport',
  SidebarItem
>;

export const sidebarList = (
  enterprise?: Enterprise,
  me?: ManagerAccountMe
): Sidebar => {
  const {
    OrdersDataV2,
    PosConnect,
    MosPosResendList,
    MosPosMenuMasterList,
    menuManagementItems,
    menuManagementCategories,
    menuManagementOptionGroups,
    menuManagementMenus,
    menuManagementAllocations,
    menuManagementExecutions,
    menuManagementBusinessHours,
    storeManagementAuthStores,
    storeManagementBrands,
    storeManagementMasterStores,
    SettingsMemberManagement,
    deliveryOutsource,
    deliveryInHouse,
    CamelOrderSites,
    CamelKioskAccounts,
    tempHourList,
    camelOrderCoupons,
    deliveryStoresStatusManagement,
    SalesReport,
    ItemsReport,
    SalesRates,
  } = routes;

  const existEnterpriseProviders = (enterprise?: Enterprise) => {
    if (enterprise === undefined) return false;
    return enterprise.providers.length > 0;
  };

  const isAllowAccess = (available_feature: AvailableFeatures) => {
    return me?.available_features.includes(available_feature) ?? false;
  };
  const isAllowAccessSalesReportPage = isAllowAccess('sales_report');

  const isAllowAccessManageAccountPage = isAllowAccess('manage_account');
  const isAllowAccessAssociateWithPosSystemPage = isAllowAccess(
    'associate_with_pos_system'
  );
  const isAllowAccessStoreManagementPage = isAllowAccess('store_management');
  const isAllowAccessMenuManagementPage = isAllowAccess('menu_management');
  const isAllowAccessCamelOrderPage = isAllowAccess('camel_order_site_setting');
  const isAllowAccessCamelKioskPage = isAllowAccess(
    'camel_kiosk_account_management'
  );
  const isAllowAccessCSVDownloadPage = isAllowAccess('order_csv_export');
  const isAllowAccessDeliveryStoresStatusManagementPage = isAllowAccess(
    'delivery_stores_status_management'
  );

  const isManager = me?.role_type === '管理者';
  const canUsePosConnectPage = (() => {
    return (
      isManager &&
      existEnterpriseProviders(enterprise) &&
      isAllowAccessAssociateWithPosSystemPage
    );
  })();

  return {
    salesReport: {
      childPageList: [
        {
          title: SalesReport.title,
          path: SalesReport.path,
          isDisabled: !isAllowAccessSalesReportPage,
        },
        {
          title: ItemsReport.title,
          path: ItemsReport.path,
          isDisabled: !isAllowAccessSalesReportPage,
        },
        {
          title: SalesRates.title,
          path: SalesRates.path,
          isDisabled: !isAllowAccessSalesReportPage,
        },
      ],
    },
    ordersData: {
      childPageList: [
        {
          title: OrdersDataV2.title,
          path: OrdersDataV2.path,
          isDisabled: !isAllowAccessCSVDownloadPage,
        },
      ],
    },
    camelOrder: {
      childPageList: [
        {
          title: CamelOrderSites.title,
          path: CamelOrderSites.path,
          isDisabled: !isAllowAccessCamelOrderPage,
        },
        {
          title: tempHourList.title,
          path: tempHourList.path,
          isDisabled: !isAllowAccessCamelOrderPage,
        },
        {
          title: camelOrderCoupons.title,
          path: camelOrderCoupons.path,
          isDisabled: !isAllowAccessCamelOrderPage,
        },
      ],
    },
    camelKiosk: {
      childPageList: [
        {
          title: CamelKioskAccounts.title,
          path: CamelKioskAccounts.path,
          isDisabled: !isAllowAccessCamelKioskPage,
        },
      ],
    },
    menuManagement: {
      childPageList: [
        {
          title: menuManagementMenus.title,
          path: menuManagementMenus.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementCategories.title,
          path: menuManagementCategories.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementItems.title,
          path: menuManagementItems.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementOptionGroups.title,
          path: menuManagementOptionGroups.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementBusinessHours.title,
          path: menuManagementBusinessHours.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementAllocations.title,
          path: menuManagementAllocations.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
        {
          title: menuManagementExecutions.title,
          path: menuManagementExecutions.path,
          isDisabled: !isAllowAccessMenuManagementPage,
        },
      ],
    },
    store: {
      childPageList: [
        {
          title: storeManagementMasterStores.title,
          path: storeManagementMasterStores.path,
          isDisabled: !isAllowAccessStoreManagementPage,
        },
        {
          title: storeManagementAuthStores.title,
          path: storeManagementAuthStores.path,
          isDisabled: !isAllowAccessStoreManagementPage,
        },
        {
          title: storeManagementBrands.title,
          path: storeManagementBrands.path,
          isDisabled: !isAllowAccessStoreManagementPage,
        },
        {
          title: deliveryStoresStatusManagement.title,
          path: deliveryStoresStatusManagement.path,
          isDisabled: !isAllowAccessDeliveryStoresStatusManagementPage,
        },
      ],
    },
    external: {
      childPageList: [
        {
          title: PosConnect.title,
          path: PosConnect.path,
          isDisabled: !canUsePosConnectPage,
        },
        // MOS POS連携は、公開対象の企業の管理者にのみ項目を出します。
        // isDisabled による無効化では、描画側がグレー表示するだけで項目の存在が
        // 見えてしまうため、対象外の企業では配列へ入れません。
        ...(canUseMosPosResendPage(me)
          ? [
              {
                title: MosPosResendList.title,
                path: MosPosResendList.path,
                isDisabled: false,
              },
            ]
          : []),
        ...(canUseMosPosMenuMasterPage(me)
          ? [
              {
                title: MosPosMenuMasterList.title,
                path: MosPosMenuMasterList.path,
                isDisabled: false,
              },
            ]
          : []),
      ],
    },
    delivery: {
      childPageList: [
        {
          title: deliveryOutsource.title,
          path: deliveryOutsource.path,
          isDisabled: !isAllowAccessCamelOrderPage,
        },
        {
          title: deliveryInHouse.title,
          path: deliveryInHouse.path,
          isDisabled: !isAllowAccessCamelOrderPage,
        },
      ],
    },
    setting: {
      childPageList: [
        {
          title: SettingsMemberManagement.title,
          path: SettingsMemberManagement.path,
          isDisabled: !isAllowAccessManageAccountPage,
        },
      ],
    },
  };
};
